/*
 * Technical Indicator Calculations (TypeScript, no external deps)
 * Implements parameter-free core functions described in instructions/index.md:
 *   – EMA (any period)
 *   – RSI with strict 30 / 70 thresholds
 *   – MACD using DI→EMA→DIF pipeline and priority-based signal rules
 *   – Multi-period EMA cross signals (n1/n2/n3/n4, default 5/10/20/60)
 *   – Aggregate computeSignals helper
 *
 * 新增技术指标:
 *   – Stochastic %K, %D
 *   – SMA 5, 10, 20, 30, 60
 *   – SMA 5, 10, 20, 30, 60 趋势信号
 *   – SMA 5, 10, 20, 30, 60 关系信号(看涨/看跌)
 */

export interface OHLCV {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface IndicatorRow extends OHLCV {
  DI?: number;
  RSI?: number;
  EMA?: Record<number, number>; // key: period
  SMA?: Record<number, number>; // key: period
  DIF?: number;
  DEA?: number;
  MACD?: number; // MACD柱状图
  RSI_sig?: 1 | 0 | -1;
  EMA_sig?: 1 | 0 | -1;
  MACD_sig?: 1 | 0 | -1;
  // 新增指标
  SMA_sig?: 1 | 0 | -1; // SMA交叉信号
  SMA_trend_sig?: 1 | 0 | -1; // SMA趋势信号
  SMA_relation_sig?: 1 | 0 | -1; // SMA关系信号
  StochasticK?: number; // Stochastic %K值
  Stochastic_sig?: 1 | 0 | -1; // Stochastic %K信号
}

/* -------------------- Helpers -------------------- */

/**
 * 计算指数移动平均线 (EMA)
 * @param series 价格序列数组
 * @param period 周期参数
 * @returns EMA值数组
 */
function ema(series: number[], period: number): number[] {
  const out: number[] = new Array(series.length).fill(NaN);
  if (!series.length || period <= 0) {
    return out;
  }
  const alpha = 2 / (period + 1);

  // 找到第一个非 NaN 的值作为初始 prev
  const firstValid = series.find((v) => !isNaN(v));
  if (firstValid === undefined) {
    return out;
  }
  let prev = firstValid;

  for (let i = 0; i < series.length; i++) {
    if (isNaN(series[i])) {
      out[i] = NaN;
      continue;
    }
    prev = i === 0 ? series[i] : alpha * series[i] + (1 - alpha) * prev;
    if (i >= period - 1) {
      out[i] = prev;
    }
  }
  return out;
}

/**
 * 计算简单移动平均线 (SMA)
 * @param series 数据序列，支持数字或字符串类型的数值
 * @param period 周期
 * @param strictMode 严格模式，true时要求连续有效数据，false时允许部分有效数据
 * @returns SMA值数组
 */
function sma(
  series: (number | string)[],
  period: number,
  strictMode = true,
): number[] {
  const out: number[] = new Array(series.length).fill(NaN);

  // 基本验证
  if (!series || series.length === 0 || period <= 0) {
    return out;
  }

  // 如果周期大于数据长度，无法计算
  if (period > series.length) {
    return out;
  }

  // 计算SMA
  for (let i = period - 1; i < series.length; i++) {
    let sum = 0;
    let validCount = 0;

    // 计算period个数据的总和
    for (let j = i - period + 1; j <= i; j++) {
      const val = series[j];

      // 统一处理数值：将字符串转换为数字，过滤无效值
      const numVal = typeof val === "string" ? Number(val) : val;

      if (typeof numVal === "number" && !isNaN(numVal) && isFinite(numVal)) {
        sum += numVal;
        validCount++;
      }
    }

    // 根据模式决定是否计算平均值
    if (strictMode) {
      // 严格模式：只有当有效数据数量等于周期时才计算平均值
      if (validCount === period) {
        out[i] = sum / period;
      }
    } else {
      // 宽松模式：只要有有效数据就计算平均值
      if (validCount > 0) {
        out[i] = sum / validCount;
      }
    }
  }
  return out;
}

/* -------------------- DI (Typical price variant) -------------------- */
function calcDI(data: OHLCV[]): number[] {
  return data.map((d) => (d.high + d.low + 2 * d.close) / 4);
}

/* -------------------- RSI -------------------- */
export function computeRSI(close: number[], period = 14): number[] {
  const rsi: number[] = new Array(close.length).fill(NaN);
  if (close.length === 0) {
    return rsi;
  }

  const gains: number[] = [];
  const losses: number[] = [];
  for (let i = 0; i < close.length; i++) {
    if (i === 0) {
      gains.push(0);
      losses.push(0);
    } else {
      const diff = close[i] - close[i - 1];
      gains.push(Math.max(diff, 0));
      losses.push(Math.max(-diff, 0));
    }
  }

  const avgGain = ema(gains, period);
  const avgLoss = ema(losses, period);

  for (let i = 0; i < close.length; i++) {
    if (i < period) {
      continue;
    }
    const rs = avgLoss[i] === 0 ? Infinity : avgGain[i] / avgLoss[i];
    rsi[i] = 100 - 100 / (1 + rs);
  }
  return rsi;
}

export function generateRSISignal(
  rsiArr: number[],
  low = 30,
  high = 70,
): (1 | 0 | -1)[] {
  return rsiArr.map((v) => (isNaN(v) ? 0 : v < low ? 1 : v > high ? -1 : 0));
}

/* -------------------- MACD (DI variant) -------------------- */
export interface MACDOpts {
  fast?: number;
  slow?: number;
  signal?: number;
}
export function computeMACD(
  data: OHLCV[],
  opts: MACDOpts = {},
): { dif: number[]; dea: number[]; macd: number[] } {
  const fast = opts.fast ?? 12;
  const slow = opts.slow ?? 26;
  const sigP = opts.signal ?? 9;
  const di = calcDI(data);
  const emaFast = ema(di, fast);
  const emaSlow = ema(di, slow);
  const dif = emaFast.map((v, i) => v - emaSlow[i]);
  const dea = ema(dif, sigP);
  // 计算MACD柱状图（标准公式：2 * (DIF - DEA)）
  const macd = dif.map((v, i) => (v - dea[i]));
  return { dif, dea, macd };
}

export function generateMACDSignal(
  dif: number[],
  dea: number[],
): (1 | 0 | -1)[] {
  const n = dif.length;
  const out: (1 | 0 | -1)[] = new Array(n).fill(0);
  for (let i = 1; i < n; i++) {
    if (
      isNaN(dif[i - 1]) ||
      isNaN(dea[i - 1]) ||
      isNaN(dif[i]) ||
      isNaN(dea[i])
    ) {
      continue;
    }

    // Priority 8 → 1 rules
    if (
      dea[i] < 0 &&
      dif[i] < 0 &&
      dif[i - 1] > dea[i - 1] &&
      dif[i] < dea[i]
    ) {
      out[i] = -1; // 8
    } else if (
      dea[i] > 0 &&
      dif[i] > 0 &&
      dif[i - 1] < dea[i - 1] &&
      dif[i] > dea[i]
    ) {
      out[i] = 1; // 7
    } else if (dif[i - 1] > 0 && dif[i] < 0 && dif[i] < dea[i]) {
      out[i] = -1; // 6
    } else if (dif[i - 1] < 0 && dif[i] > 0 && dif[i] > dea[i]) {
      out[i] = 1; // 5
    } else if (dif[i] < dea[i]) {
      out[i] = -1; // 4
    } else if (dif[i] > dea[i]) {
      out[i] = 1; // 3
    } else if (dea[i] < 0 && dif[i] < 0) {
      out[i] = -1; // 2
    } else if (dea[i] > 0 && dif[i] > 0) {
      out[i] = 1; // 1
    }
  }
  return out;
}

/* -------------------- Multi-period EMA cross -------------------- */
export function computeEMAs(
  close: number[],
  periods: number[],
): Record<number, number[]> {
  const map: Record<number, number[]> = {};
  periods.forEach((p) => {
    map[p] = ema(close, p);
  });
  return map;
}

export function generateEMACrossSignal(
  emaDict: Record<number, number[]>,
  periods: number[],
): (1 | 0 | -1)[] {
  const len = emaDict[periods[0]].length;
  const out: (1 | 0 | -1)[] = new Array(len).fill(0);

  for (let i = 1; i < len; i++) {
    // helper
    const cross = (pFast: number, pSlow: number): 1 | -1 | 0 => {
      const fastPrev = emaDict[pFast][i - 1];
      const slowPrev = emaDict[pSlow][i - 1];
      const fastNow = emaDict[pFast][i];
      const slowNow = emaDict[pSlow][i];
      if (
        isNaN(fastPrev) ||
        isNaN(slowPrev) ||
        isNaN(fastNow) ||
        isNaN(slowNow)
      ) {
        return 0;
      }
      if (fastPrev < slowPrev && fastNow > slowNow) {
        return 1;
      }
      if (fastPrev > slowPrev && fastNow < slowNow) {
        return -1;
      }
      return 0;
    };

    let sig: 1 | -1 | 0 = 0;
    if (periods.length === 4) {
      // long→mid→short
      sig = cross(periods[2], periods[3]);
      if (sig === 0) {
        sig = cross(periods[1], periods[2]);
      }
      if (sig === 0) {
        sig = cross(periods[0], periods[1]);
      }
    } else if (periods.length === 3) {
      // long→short
      sig = cross(periods[1], periods[2]);
      if (sig === 0) {
        sig = cross(periods[0], periods[1]);
      }
    } else if (periods.length === 2) {
      sig = cross(periods[0], periods[1]);
    }
    out[i] = sig;
  }
  return out;
}

/* -------------------- Multi-period SMA cross -------------------- */

/**
 * 计算多个周期的简单移动平均线 (SMA)
 * @param close 收盘价数组，支持数字或字符串类型的数值
 * @param periods 周期数组，如 [5, 10, 20]
 * @returns 包含各周期SMA的对象
 */
export function computeSMAs(
  close: (number | string)[],
  periods: number[],
): Record<number, number[]> {
  const map: Record<number, number[]> = {};

  // 验证输入数据
  if (!close || close.length === 0) {
    return map;
  }

  // 计算每个周期的SMA
  periods.forEach((p) => {
    if (p > 0) {
      map[p] = sma(close, p);
    }
  });

  return map;
}

/**
 * 生成SMA交叉信号
 * @param smaDict 包含各周期SMA的对象
 * @param periods 周期数组，按从短到长排序
 * @returns 信号数组 (1: 看涨信号, -1: 看跌信号, 0: 无信号)
 *
 * SMA交叉信号逻辑：
 * 1. 短期SMA向上穿越长期SMA = 看涨信号（金叉）
 * 2. 短期SMA向下穿越长期SMA = 看跌信号（死叉）
 * 3. 优先级：SMA5/SMA20 > SMA10/SMA20 > SMA5/SMA10
 */
export function generateSMACrossSignal(
  smaDict: Record<number, number[]>,
  periods: number[],
): (1 | 0 | -1)[] {
  // 找到第一个有效的SMA数组来确定长度
  const firstValidPeriod = periods.find(
    (p) => smaDict[p] && smaDict[p].length > 0,
  );
  if (!firstValidPeriod) {
    return [];
  }

  const len = smaDict[firstValidPeriod].length;
  const out: (1 | 0 | -1)[] = new Array(len).fill(0);

  for (let i = 1; i < len; i++) {
    // 辅助函数：检测两条SMA线的交叉
    const cross = (pFast: number, pSlow: number): 1 | -1 | 0 => {
      // 检查SMA数组是否存在
      if (!smaDict[pFast] || !smaDict[pSlow]) {
        return 0;
      }

      const fastPrev = smaDict[pFast][i - 1];
      const slowPrev = smaDict[pSlow][i - 1];
      const fastNow = smaDict[pFast][i];
      const slowNow = smaDict[pSlow][i];

      if (
        isNaN(fastPrev) ||
        isNaN(slowPrev) ||
        isNaN(fastNow) ||
        isNaN(slowNow)
      ) {
        return 0;
      }

      // 金叉：短期SMA向上穿越长期SMA
      if (fastPrev <= slowPrev && fastNow > slowNow) {
        return 1;
      }
      // 死叉：短期SMA向下穿越长期SMA
      if (fastPrev >= slowPrev && fastNow < slowNow) {
        return -1;
      }
      return 0;
    };

    let sig: 1 | -1 | 0 = 0;

    // 根据周期数量生成不同的交叉信号
    if (periods.length === 4) {
      // 四线交叉（如SMA5/SMA10/SMA20/SMA60）
      // 优先级：SMA5/SMA20 > SMA10/SMA20 > SMA5/SMA10 > SMA20/SMA60
      sig = cross(periods[0], periods[2]); // SMA5/SMA20
      if (sig === 0) {
        sig = cross(periods[1], periods[2]); // SMA10/SMA20
      }
      if (sig === 0) {
        sig = cross(periods[0], periods[1]); // SMA5/SMA10
      }
      if (sig === 0) {
        sig = cross(periods[2], periods[3]); // SMA20/SMA60
      }
    } else if (periods.length === 3) {
      // SMA5/SMA10/SMA20 三线交叉
      // 优先级：SMA5/SMA20 > SMA10/SMA20 > SMA5/SMA10
      sig = cross(periods[0], periods[2]); // SMA5/SMA20
      if (sig === 0) {
        sig = cross(periods[1], periods[2]); // SMA10/SMA20
      }
      if (sig === 0) {
        sig = cross(periods[0], periods[1]); // SMA5/SMA10
      }
    } else if (periods.length === 2) {
      // 双线交叉
      sig = cross(periods[0], periods[1]);
    }

    out[i] = sig;
  }
  return out;
}

/**
 * 生成SMA趋势信号
 * @param close 收盘价数组，支持数字或字符串类型的数值
 * @param smaDict 包含各周期SMA的对象
 * @param periods 周期数组
 * @returns 信号数组 (1: 强势看涨, -1: 强势看跌, 0: 中性)
 *
 * SMA趋势信号逻辑：
 * 1. 价格在所有SMA之上 = 强势看涨
 * 2. 价格在所有SMA之下 = 强势看跌
 * 3. 价格在SMA之间 = 中性
 */
export function generateSMATrendSignal(
  close: (number | string)[],
  smaDict: Record<number, number[]>,
  periods: number[],
): (1 | 0 | -1)[] {
  const len = close.length;
  const out: (1 | 0 | -1)[] = new Array(len).fill(0);

  for (let i = 0; i < len; i++) {
    const currentPrice =
      typeof close[i] === "string" ? Number(close[i]) : close[i];

    // 检查当前价格是否有效
    if (
      typeof currentPrice !== "number" ||
      isNaN(currentPrice) ||
      !isFinite(currentPrice)
    ) {
      continue;
    }

    // 检查当前价格与各SMA的关系
    let aboveAllSMA = true;
    let belowAllSMA = true;

    for (const period of periods) {
      // 检查SMA数组是否存在
      if (!smaDict[period] || !smaDict[period][i]) {
        continue;
      }

      const smaValue = smaDict[period][i];
      if (!isNaN(smaValue)) {
        if (currentPrice <= smaValue) {
          aboveAllSMA = false;
        }
        if (currentPrice >= smaValue) {
          belowAllSMA = false;
        }
      }
    }

    // 生成信号
    if (aboveAllSMA) {
      out[i] = 1; // 强势看涨
    } else if (belowAllSMA) {
      out[i] = -1; // 强势看跌
    } else {
      out[i] = 0; // 中性
    }
  }

  return out;
}

/* -------------------- 新增: 指标 -------------------- */

/**
 * 计算KD指标（随机指标, Stochastic Oscillator）
 * @param data OHLCV数据
 * @param period 计算周期，默认14（常用周期）
 * @param smoothK K值平滑系数，默认3
 * @param smoothD D值平滑系数，默认3
 *
 * KD指标组成：
 * 1. %K线（快线）：反映价格在最近一段时间内的相对位置
 *    公式：%K = [(C - L14) / (H14 - L14)] × 100
 * 2. %D线（慢线）：%K线的移动平均线，通常是3天的简单移动平均(SMA)
 *    公式：%D = (%K的n日总和) / n (n通常为3)
 *
 * 值范围：0-100之间，反映价格的波动情况
 */
/**
 * 计算 Stochastic %K 指标
 * @param data OHLCV数据数组
 * @param period 计算周期，默认14
 * @param smoothK K值平滑系数，默认3
 * @returns %K值数组
 *
 * 平滑处理说明：
 * 使用SMA（简单移动平均）算法对RSV值进行平滑处理
 */
export function calculateStochasticK(
  data: OHLCV[],
  period = 14,
  smoothK = 3,
): number[] {
  const K: number[] = new Array(data.length).fill(NaN);

  if (data.length < period) {
    return K;
  }

  // 计算RSV数组
  const rsvArray: number[] = new Array(data.length).fill(NaN);

  for (let i = period - 1; i < data.length; i++) {
    // 获取最近period天的数据
    const slice = data.slice(i - period + 1, i + 1);
    const highs = slice.map((d) => d.high);
    const lows = slice.map((d) => d.low);
    const currentClose = data[i].close;

    // 计算最高价和最低价
    const highestHigh = Math.max(...highs);
    const lowestLow = Math.min(...lows);

    // 判空处理：避免除零错误
    const priceRange = highestHigh - lowestLow;
    if (priceRange === 0) {
      // 当最高价等于最低价时，RSV设为50（中性值）
      rsvArray[i] = 50;
      continue;
    }

    // 计算RSV (Raw Stochastic Value)
    rsvArray[i] = ((currentClose - lowestLow) / priceRange) * 100;
  }
  // 使用SMA算法进行平滑处理
  // 使用非严格模式来处理Stochastic的数据模式
  return sma(rsvArray, smoothK, false);
}

/**
 * 生成 Stochastic %K 信号
 * @param K %K值数组
 * @param overbought 超买阈值，默认80
 * @param oversold 超卖阈值，默认20
 * @returns 信号数组 (1: 看涨信号, -1: 看跌信号, 0: 无信号)
 *
 * 信号判断逻辑：
 * - 超买区域（%K > 80）：价格可能过高，市场可能进入回调或下跌阶段
 * - 超卖区域（%K < 20）：价格可能过低，市场可能反弹或上涨
 * - 正常区域（20 ≤ %K ≤ 80）：中性，无明显方向
 */
export function generateStochasticSignal(
  K: number[],
  overbought = 80,
  oversold = 20,
): (1 | 0 | -1)[] {
  const signals: (1 | 0 | -1)[] = new Array(K.length).fill(0);

  for (let i = 0; i < K.length; i++) {
    if (isNaN(K[i])) {
      continue;
    }

    // 超买区域信号（看跌）
    if (K[i] > overbought) {
      signals[i] = -1;
    }
    // 超卖区域信号（看涨）
    else if (K[i] < oversold) {
      signals[i] = 1;
    }
    // 正常区域（中性）
    else {
      signals[i] = 0;
    }
  }

  return signals;
}

/**
 * 分析SMA关系并生成市场情绪信号
 * @param smaDict 包含各周期SMA的对象
 * @param periods 周期数组
 * @returns 市场情绪信号数组 (1: 看涨, -1: 看跌, 0: 中性)
 *
 * 基于SMA关系规则：
 * - SMA5 > SMA10: 看涨
 * - SMA5 < SMA10: 看跌
 * - SMA5 = SMA10: 中性
 * - 同样适用于SMA10/SMA20, SMA20/SMA30, SMA30/SMA60
 */
export function generateSMARelationSignal(
  smaDict: Record<number, number[]>,
  periods: number[],
): (1 | 0 | -1)[] {
  const firstValidPeriod = periods.find(
    (p) => smaDict[p] && smaDict[p].length > 0,
  );
  if (!firstValidPeriod) {
    return [];
  }

  const len = smaDict[firstValidPeriod].length;
  const out: (1 | 0 | -1)[] = new Array(len).fill(0);

  for (let i = 0; i < len; i++) {
    let bullishCount = 0;
    let bearishCount = 0;
    let totalRelations = 0;

    // 分析所有SMA关系
    for (let j = 0; j < periods.length - 1; j++) {
      const shortPeriod = periods[j];
      const longPeriod = periods[j + 1];

      if (!smaDict[shortPeriod] || !smaDict[longPeriod]) {
        continue;
      }

      const shortValue = smaDict[shortPeriod][i];
      const longValue = smaDict[longPeriod][i];

      if (isNaN(shortValue) || isNaN(longValue)) {
        continue;
      }

      totalRelations++;

      // 判断关系
      if (shortValue > longValue) {
        bullishCount++;
      } else if (shortValue < longValue) {
        bearishCount++;
      }
      // 相等的情况不计入任何一方
    }

    // 生成综合信号
    if (totalRelations === 0) {
      out[i] = 0;
    } else if (bullishCount > bearishCount) {
      out[i] = 1; // 看涨
    } else if (bearishCount > bullishCount) {
      out[i] = -1; // 看跌
    } else {
      out[i] = 0; // 中性
    }
  }

  return out;
}

/* -------------------- Aggregate -------------------- */
export interface SignalOpts {
  rsiPeriod?: number;
  emaPeriods?: number[];
  macdFast?: number;
  macdSlow?: number;
  macdSignal?: number;
  // 新增参数
  supportResistanceLookback?: number;
  fibonacciThreshold?: number; // 斐波那契识别阈值
  trendPeriod?: number; // 趋势识别周期
  volumePeriod?: number;
  bbPeriod?: number;
  bbStdDev?: number;
  stochasticPeriod?: number; // Stochastic计算周期
  stochasticSmoothK?: number; // K值平滑系数
  smaPeriods?: number[]; // SMA周期数组
}

export function computeSignals(
  data: OHLCV[],
  opts: SignalOpts = {},
): IndicatorRow[] {
  const close = data.map((d) => d.close);
  const rsiPeriod = opts.rsiPeriod ?? 14;
  const emaPs: number[] = opts.emaPeriods ?? [5, 10, 20, 60];
  const smaPs: number[] = opts.smaPeriods ?? [5, 10, 20, 60];

  // RSI
  const rsiArr = computeRSI(close, rsiPeriod);
  const rsiSig = generateRSISignal(rsiArr);

  // EMA
  const emaDict = computeEMAs(close, emaPs);
  const emaSig = generateEMACrossSignal(emaDict, emaPs);

  // MACD
  const { dif, dea, macd } = computeMACD(data);
  const macdSig = generateMACDSignal(dif, dea);

  // SMA
  const smaDict = computeSMAs(close, smaPs);
  const smaSig = generateSMACrossSignal(smaDict, smaPs);
  const smaTrendSig = generateSMATrendSignal(close, smaDict, smaPs);
  const smaRelationSig = generateSMARelationSignal(smaDict, smaPs);

  // Stochastic %K 指标 随机指标%K值
  const stochasticK = calculateStochasticK(
    data,
    opts.stochasticPeriod ?? 14,
    opts.stochasticSmoothK ?? 3,
  );
  const stochasticSig = generateStochasticSignal(stochasticK);

  // merge results row-wise
  return data.map((d, i) => {
    const emaSnapshot: Record<number, number> = {};
    emaPs.forEach((p) => (emaSnapshot[p] = emaDict[p][i]));
    const smaSnapshot: Record<number, number> = {};
    smaPs.forEach((p) => (smaSnapshot[p] = smaDict[p][i]));

    return {
      ...d,
      DI: calcDI([d])[0],
      RSI: rsiArr[i],
      EMA: emaSnapshot,
      SMA: smaSnapshot,
      DIF: dif[i],
      DEA: dea[i],
      MACD: macd[i],
      RSI_sig: rsiSig[i],
      EMA_sig: emaSig[i],
      MACD_sig: macdSig[i],
      // 新增指标
      SMA_sig: smaSig[i],
      SMA_trend_sig: smaTrendSig[i],
      SMA_relation_sig: smaRelationSig[i],
      // Stochastic %K 指标
      StochasticK: stochasticK[i],
      Stochastic_sig: stochasticSig[i],
    };
  });
}
